import React from 'react';
import api from '../services/api';
import GpoFilter, { matchesGpo, FilterCount } from './GpoFilter';

// Cut Plan & Lay Register (spec 42fa27b, mig 153). Order-Execution tab. Document-only.
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
const inp = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const btn = { padding: '7px 14px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const SC = { draft: '#888', active: '#3B3A8F', closed: '#555', cancelled: '#B91C1C' };
const pill = (s) => <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#fff', background: SC[s] || '#888' }}>{s}</span>;
// Ruling #7 honesty tag — renders on lay DETAIL and LIST rows.
const unlinkedTag = (compact) => <span title="This lay's fabric is free-entry, not linked to a store issue slip (fabric is not issuable yet — Phase 2B)."
  style={{ fontSize: compact ? 9 : 10, color: '#B45309', background: '#FFF4E5', padding: '1px 5px', borderRadius: 4, whiteSpace: 'nowrap' }}>⚠ fabric source not slip-linked</span>;

export default function CutPlanTab({ user }) {
  const canPlan = ['admin', 'management', 'ppc_manager'].includes(user?.role);
  const [rows, setRows] = React.useState([]);
  const [gpoQ, setGpoQ] = React.useState('');   // GET /cutting/cut-plans is UNCAPPED — client-side is safe
  const [sel, setSel] = React.useState(null);       // detail plan object
  const [creating, setCreating] = React.useState(false);
  const [laying, setLaying] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const load = React.useCallback(() => { api.get('/cutting/cut-plans').then(r => setRows(r.data.data || [])).catch(() => {}); }, []);
  React.useEffect(() => { load(); }, [load]);
  const openPlan = (id) => api.get('/cutting/cut-plans/' + id).then(r => setSel(r.data.data)).catch(() => {});
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };
  const act = async (id, verb) => { try { await api.post(`/cutting/cut-plans/${id}/${verb}`); flash(verb + ' done'); openPlan(id); load(); } catch (e) { alert(e.response?.data?.error || verb + ' failed'); } };

  if (sel) return <PlanDetail plan={sel} canPlan={canPlan} onBack={() => { setSel(null); load(); }} reload={() => openPlan(sel.id)} onAct={act} onLay={() => setLaying(true)} laying={laying} setLaying={setLaying} />;

  const shown = rows.filter(r => matchesGpo(gpoQ, r.gpo_no, r.cut_plan_no));
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: '#888' }}>Cut-room plans: per-size targets (seeded from the estimate ratio) + lays. Document-only — no stock movement.</span>
        <GpoFilter value={gpoQ} onChange={setGpoQ} />
        <div>{msg && <span style={{ color: '#166534', fontSize: 12, marginRight: 10 }}>✓ {msg}</span>}{canPlan && <button onClick={() => setCreating(true)} style={btn}>+ New Cut Plan</button>}</div>
      </div>
      <FilterCount shown={shown.length} total={rows.length} noun="cut plans" q={gpoQ} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Cut Plan', 'GPO', 'Ratio', 'Sizes', 'Lays', 'Over-cut', 'Fabric', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {shown.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{rows.length ? 'No cut plan matches that GPO.' : 'No cut plans yet.'}</td></tr>}
          {shown.map(r => (
            <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openPlan(r.id)}>
              <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{r.cut_plan_no}</td>
              <td style={td}>{r.gpo_no}</td>
              <td style={{ ...td, fontSize: 11, color: '#888' }}>{r.ratio_source}</td>
              <td style={{ ...td, textAlign: 'center' }}>{r.size_count}</td>
              <td style={{ ...td, textAlign: 'center' }}>{r.lay_count}</td>
              <td style={{ ...td, textAlign: 'center' }}>{num(r.over_cut_sizes) > 0 ? <span style={{ color: '#B91C1C', fontWeight: 700 }}>⚠ {r.over_cut_sizes}</span> : '—'}</td>
              <td style={td}>{r.has_unlinked_fabric ? unlinkedTag(true) : '—'}</td>
              <td style={td}>{pill(r.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <NewPlan onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); load(); openPlan(id); }} />}
    </div>
  );
}

function NewPlan({ onClose, onSaved }) {
  const [gpo, setGpo] = React.useState(''); const [unit, setUnit] = React.useState('KG');
  const [err, setErr] = React.useState(''); const [busy, setBusy] = React.useState(false);
  const save = async () => {
    setErr(''); if (!gpo.trim()) return setErr('GPO no is required.');
    setBusy(true);
    try { const r = await api.post('/cutting/cut-plans', { gpo_no: gpo.trim(), fabric_unit: unit }); onSaved(r.data.data.id); }
    catch (e) { setErr(e.response?.data?.error || 'Create failed'); } finally { setBusy(false); }
  };
  return (
    <Modal title="New Cut Plan" onClose={onClose}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>Sizes seed from the GPO's estimate ratio (editable before activation).</div>
      <label style={{ fontSize: 12, color: '#555' }}>GPO no *<br /><input value={gpo} onChange={e => setGpo(e.target.value)} style={{ ...inp, width: '100%', margin: '3px 0 10px' }} placeholder="e.g. 1829" /></label>
      <label style={{ fontSize: 12, color: '#555' }}>Fabric unit<br />
        <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...inp, width: '100%', margin: '3px 0 10px' }}><option>KG</option><option>MTR</option></select></label>
      {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <Actions onClose={onClose} onSave={save} busy={busy} label="Create" />
    </Modal>
  );
}

function PlanDetail({ plan, canPlan, onBack, reload, onAct, onLay, laying, setLaying }) {
  return (
    <div>
      <button onClick={onBack} style={{ ...btn, background: '#f0f2f8', color: '#555', marginBottom: 12 }}>← Back</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#3B3A8F' }}>{plan.cut_plan_no} {pill(plan.status)}</div>
          <div style={{ fontSize: 12, color: '#888' }}>GPO {plan.gpo_no} · ratio: {plan.ratio_source} · fabric in {plan.fabric_unit}</div>
        </div>
        {canPlan && <div style={{ display: 'flex', gap: 8 }}>
          {plan.status === 'draft' && <button onClick={() => onAct(plan.id, 'activate')} style={btn}>Activate</button>}
          {plan.status === 'active' && <button onClick={onLay} style={btn}>+ Add Lay</button>}
          {plan.status === 'active' && <button onClick={() => onAct(plan.id, 'close')} style={{ ...btn, background: '#555' }}>Close</button>}
        </div>}
      </div>

      <h4 style={{ margin: '14px 0 6px', color: '#333' }}>Per-size coverage</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead><tr>{['Size', 'Target', 'Cut', 'Over-cut?'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {(plan.coverage || []).map(c => (
            <tr key={c.size_label} style={{ background: c.over_cut ? '#FEF2F2' : '#fff' }}>
              <td style={{ ...td, fontWeight: 600 }}>{c.size_label}</td>
              <td style={{ ...td, textAlign: 'right' }}>{num(c.target_qty)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{num(c.cut_qty)}</td>
              <td style={td}>{c.over_cut ? <span style={{ color: '#B91C1C', fontWeight: 700 }}>⚠ over 3%</span> : '—'}</td>
            </tr>
          ))}
          {(plan.coverage || []).length === 0 && <tr><td colSpan={4} style={{ ...td, color: '#aaa' }}>No sizes seeded — edit before activation.</td></tr>}
        </tbody>
      </table>

      <h4 style={{ margin: '14px 0 6px', color: '#333' }}>Lays</h4>
      {(plan.lays || []).length === 0 && <div style={{ color: '#aaa', fontSize: 13, marginBottom: 12 }}>No lays yet.</div>}
      {(plan.lays || []).map(l => (
        <div key={l.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <b style={{ color: '#3B3A8F', fontFamily: 'monospace' }}>{l.lay_no}</b>
            {l.has_unlinked_fabric && unlinkedTag(false)}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            {l.plies} plies · efficiency {l.efficiency_pct != null ? l.efficiency_pct + '%' : '—'} · fabric {l.fabric_consumed != null ? num(l.fabric_consumed) + ' ' + plan.fabric_unit : '—'}{num(l.end_bit_qty) > 0 ? ` · end-bit ${num(l.end_bit_qty)} ${plan.fabric_unit}` : ''}
          </div>
          <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>Cut: {(l.sizes || []).map(s => `${s.size_label}×${num(s.cut_qty)}`).join('  ')}</div>
          {(l.fabric_sources || []).length > 0 && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Fabric: {(l.fabric_sources || []).map(f => `${f.fabric_desc || 'roll'} ${num(f.qty)}${f.unit || ''}${f.slip_line_id ? ' (slip)' : ''}`).join(', ')}</div>}
        </div>
      ))}
      {laying && <AddLay plan={plan} onClose={() => setLaying(false)} onSaved={() => { setLaying(false); reload(); }} />}
    </div>
  );
}

function AddLay({ plan, onClose, onSaved }) {
  const [plies, setPlies] = React.useState(''); const [eff, setEff] = React.useState(''); const [marker, setMarker] = React.useState('');
  const [consumed, setConsumed] = React.useState(''); const [endbit, setEndbit] = React.useState('');
  const [cut, setCut] = React.useState({});           // size_label -> qty
  const [fab, setFab] = React.useState([{ fabric_desc: '', rolls: '', qty: '', unit: plan.fabric_unit }]);
  const [err, setErr] = React.useState(''); const [busy, setBusy] = React.useState(false); const [warn, setWarn] = React.useState(null);
  const save = async () => {
    setErr('');
    if (!(num(plies) > 0)) return setErr('Plies must be > 0.');
    if (eff && (num(eff) < 40 || num(eff) > 95)) return setErr('Efficiency must be between 40 and 95.');
    const sizes = (plan.sizes || []).map(s => ({ size_label: s.size_label, cut_qty: num(cut[s.size_label]) })).filter(s => s.cut_qty > 0);
    if (!sizes.length) return setErr('Enter a cut qty for at least one size.');
    setBusy(true);
    try {
      const r = await api.post(`/cutting/cut-plans/${plan.id}/lays`, {
        plies: num(plies), efficiency_pct: eff ? num(eff) : null, marker_length: marker ? num(marker) : null,
        fabric_consumed: consumed ? num(consumed) : null, end_bit_qty: endbit ? num(endbit) : 0, sizes,
        fabric_sources: fab.filter(f => f.fabric_desc || f.qty).map(f => ({ fabric_desc: f.fabric_desc, rolls: f.rolls ? num(f.rolls) : null, qty: f.qty ? num(f.qty) : null, unit: f.unit })),
      });
      if (r.data.data.warnings && r.data.data.warnings.length) { setWarn(r.data.data.warnings); setTimeout(onSaved, 3500); }
      else onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Add lay failed'); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Add Lay to ${plan.cut_plan_no}`} onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 10 }}>
        <Field label="Plies *"><input type="number" value={plies} onChange={e => setPlies(e.target.value)} style={{ ...inp, width: '100%' }} /></Field>
        <Field label="Efficiency % (40-95)"><input type="number" value={eff} onChange={e => setEff(e.target.value)} style={{ ...inp, width: '100%' }} /></Field>
        <Field label={`Marker (${plan.fabric_unit})`}><input type="number" value={marker} onChange={e => setMarker(e.target.value)} style={{ ...inp, width: '100%' }} /></Field>
        <Field label={`Fabric used (${plan.fabric_unit})`}><input type="number" value={consumed} onChange={e => setConsumed(e.target.value)} style={{ ...inp, width: '100%' }} /></Field>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', margin: '8px 0 4px' }}>Cut qty per size</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {(plan.sizes || []).map(s => (
          <div key={s.size_label} style={{ width: 90 }}>
            <div style={{ fontSize: 11, color: '#888' }}>{s.size_label} <span style={{ color: '#bbb' }}>/{num(s.ratio_qty)}</span></div>
            <input type="number" value={cut[s.size_label] || ''} onChange={e => setCut(c => ({ ...c, [s.size_label]: e.target.value }))} style={{ ...inp, width: '100%' }} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', margin: '8px 0 4px' }}>Fabric sources <span style={{ fontWeight: 400, color: '#B45309', fontSize: 11 }}>· free-entry (not slip-linked until fabric is issuable)</span></div>
      {fab.map((f, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 6, marginBottom: 6 }}>
          <input placeholder="fabric desc" value={f.fabric_desc} onChange={e => setFab(a => a.map((x, j) => j === i ? { ...x, fabric_desc: e.target.value } : x))} style={inp} />
          <input type="number" placeholder="rolls" value={f.rolls} onChange={e => setFab(a => a.map((x, j) => j === i ? { ...x, rolls: e.target.value } : x))} style={inp} />
          <input type="number" placeholder="qty" value={f.qty} onChange={e => setFab(a => a.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} style={inp} />
          <select value={f.unit} onChange={e => setFab(a => a.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} style={inp}><option>KG</option><option>MTR</option></select>
        </div>
      ))}
      <button onClick={() => setFab(a => [...a, { fabric_desc: '', rolls: '', qty: '', unit: plan.fabric_unit }])} style={{ ...btn, background: '#eef', color: '#3B3A8F', marginBottom: 10 }}>+ fabric</button>
      <Field label={`End-bit (${plan.fabric_unit}, document-only)`}><input type="number" value={endbit} onChange={e => setEndbit(e.target.value)} style={{ ...inp, width: 160 }} /></Field>
      {warn && <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 6, padding: 10, margin: '10px 0', fontSize: 12, color: '#B91C1C' }}>⚠ Over-cut: {warn.map((w, i) => <div key={i}>{w}</div>)}</div>}
      {err && <div style={{ color: '#B91C1C', fontSize: 13, margin: '10px 0' }}>{err}</div>}
      <Actions onClose={onClose} onSave={save} busy={busy} label="Add lay" />
    </Modal>
  );
}

const Modal = ({ title, onClose, children, wide }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
    <div style={{ background: '#fff', borderRadius: 12, width: '92%', maxWidth: wide ? 760 : 460, padding: 22, maxHeight: '88vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}><b style={{ fontSize: 16 }}>{title}</b><button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button></div>
      {children}
    </div>
  </div>
);
const Field = ({ label, children }) => <label style={{ fontSize: 11, color: '#555' }}>{label}<br />{children}</label>;
const Actions = ({ onClose, onSave, busy, label }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
    <button onClick={onClose} style={{ ...btn, background: '#f0f2f8', color: '#555' }}>Cancel</button>
    <button onClick={onSave} disabled={busy} style={{ ...btn, opacity: busy ? 0.5 : 1 }}>{busy ? '…' : label}</button>
  </div>
);
